import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PhotoModal from "../../calendar/progress-photos/PhotoModal";
import CompareProgressPhotosModal from "./CompareProgressPhotosModal";
import {
  findFrontLeftBack,
  mapPhotos,
} from "../../calendar/body-composition/body-composition.helper";
import SvgIcon from "../../../../parts/SvgIcon";

const QuestionProgressPictures = () => {
  const { t } = useTranslation("athlete-portal");
  return (
    <div className="question-progress-pictures">
      <div className="progress-picture">
        <SvgIcon src={"/img/new-icons/add.svg"} />
        <span>{t("questionProgress.front")}</span>
      </div>
      <div className="progress-picture">
        <SvgIcon src={"/img/new-icons/add.svg"} />
        <span>{t("questionProgress.back")}</span>
      </div>
      <div className="progress-picture">
        <SvgIcon src={"/img/new-icons/add.svg"} />
        <span>{t("questionProgress.side")}</span>
      </div>
    </div>
  );
};

export const CreateQuestionProgressPictureBody = () => {
  return (
    <div className="answer-scale-body">
      <QuestionProgressPictures />
      <div className="no-buttons"></div>
    </div>
  );
};

const PHOTO_ANGLES = ["front", "back", "side"];

export const ProgressPictureAnswer = ({
  answer,
  dailyStats,
  previousAnswer,
  previousDailyStats,
  previousDate,
  currentDate,
}) => {
  const { t } = useTranslation("athlete-portal");

  const [modal, setModal] = useState({
    open: false,
    type: "",
    pictureImg: "",
  });

  const [compare, setCompare] = useState({ open: false, angle: null });

  const toggleModal = (pictureImg = "", type = "") => {
    setModal({
      open: !modal.open,
      type,
      pictureImg,
    });
  };

  const currentPhotos =
    getProgressPhotos(dailyStats) ||
    getProgressPhotos([{ bodyPhotos: answer?.value }]);

  const previousPhotos =
    (previousDailyStats && getProgressPhotos(previousDailyStats)) ||
    getProgressPhotos([{ bodyPhotos: previousAnswer?.answer?.value }]);

  // Without current photos there is nothing to compare against, so fall back to
  // the original behavior instead of rendering an empty "current" column.
  if (!currentPhotos) {
    return (
      <span className="question-description-short">
        {t("questionProgress.noDataToShow")}
      </span>
    );
  }

  const renderPhoto = (photo, key, onClick) =>
    photo ? (
      <img key={key} src={photo} className="question-photo" onClick={onClick} />
    ) : (
      <div key={key} className="photo-empty"></div>
    );

  // Comparison cards: render the photo, or an empty state (camera + message)
  // when that side has no photo for the angle.
  const renderComparisonSlot = (photo, slot, key) =>
    photo ? (
      <img key={key} src={photo} className="question-photo" alt="" />
    ) : (
      <div key={key} className="photo-empty">
        <SvgIcon
          src={"/img/new-icons/camera-off.svg"}
          useCurrentColor
          style={{ width: "1.75rem", height: "1.75rem" }}
        />
        <span>
          {t("questionProgress.noPhoto", {
            type: t(`questionProgress.${slot}`).toLowerCase(),
          })}
          <br />
          {t("questionProgress.toCompare")}
        </span>
      </div>
    );

  // No previous check-in (or it had no photos): keep the original single row,
  // rendering only the angles that actually have a photo.
  if (!previousPhotos) {
    return (
      <section role="progress-photos-wrapper">
        <div>
          {PHOTO_ANGLES.filter((angle) => currentPhotos?.[angle]).map((angle) =>
            renderPhoto(currentPhotos[angle], angle, () =>
              toggleModal(currentPhotos[angle], angle)
            )
          )}
        </div>
        {modal.open ? (
          <PhotoModal photo={modal} toggleModal={toggleModal} />
        ) : null}
      </section>
    );
  }

  // Any angle with at least one photo can be opened in the viewer; angles with
  // both are comparable, angles with one show that single photo large.
  const viewableAngles = PHOTO_ANGLES.filter(
    (angle) => previousPhotos?.[angle] || currentPhotos?.[angle]
  );

  return (
    <section role="progress-photos-comparison">
      <div className="progress-photos-comparison-grid">
        {PHOTO_ANGLES.map((angle) => {
          const cardContent = (
            <>
              <span className="comparison-card-pose">
                {t("questionProgress.pose", {
                  type: t(`questionProgress.${angle}`),
                })}
              </span>
              <div className="comparison-card-photos">
                {renderComparisonSlot(
                  previousPhotos?.[angle],
                  "previous",
                  `previous-${angle}`
                )}
                {renderComparisonSlot(
                  currentPhotos?.[angle],
                  "current",
                  `current-${angle}`
                )}
              </div>
              <div className="comparison-card-dates">
                <span>{previousDate}</span>
                <span>{currentDate}</span>
              </div>
            </>
          );

          // Angles with at least one photo become a clickable card that opens
          // the viewer (comparison when both exist, single photo otherwise).
          if (viewableAngles.includes(angle)) {
            const bothPresent =
              previousPhotos?.[angle] && currentPhotos?.[angle];
            return (
              <button
                type="button"
                className="comparison-card comparison-block"
                key={`${angle}-card`}
                onClick={() => setCompare({ open: true, angle })}
              >
                {cardContent}
                <span className="compare-hint">
                  <SvgIcon
                    src={"/img/new-icons/Zoom.svg"}
                    useCurrentColor
                    style={{ width: "0.875rem", height: "0.875rem" }}
                  />
                  {bothPresent
                    ? t("questionProgress.compare")
                    : t("questionProgress.view")}
                </span>
              </button>
            );
          }

          return (
            <div className="comparison-card" key={`${angle}-card`}>
              {cardContent}
            </div>
          );
        })}
      </div>
      {compare.open ? (
        <CompareProgressPhotosModal
          currentPhotos={currentPhotos}
          previousPhotos={previousPhotos}
          angles={viewableAngles}
          initialAngle={compare.angle}
          previousDate={previousDate}
          currentDate={currentDate}
          onClose={() => setCompare({ open: false, angle: null })}
        />
      ) : null}
      {modal.open ? (
        <PhotoModal photo={modal} toggleModal={toggleModal} />
      ) : null}
    </section>
  );
};

const getProgressPhotos = (dailyStats) => {
  const filteredPhotos = dailyStats
    .slice()
    .filter((stat) => stat.bodyPhotos)
    .sort((a, b) => a.utcDate - b.utcDate)
    .map(mapPhotos);

  const result = findFrontLeftBack(filteredPhotos);

  const quantityOfPhotos = Object.keys(result).length;

  return quantityOfPhotos > 0 ? result : null;
};

export default QuestionProgressPictures;
